import { AfterViewInit, Component, DestroyRef, ElementRef, EventEmitter, inject, Input, OnInit, Output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Subject, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { TreeModule, Tree } from 'primeng/tree';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SharedModule } from 'primeng/api';
import { FloatLabelModule } from 'primeng/floatlabel';
import type { TreeNode } from 'primeng/api';
import { LinkedFile, StatusOption, DropdownOption, FileItem, LinkPatch, nextTempLinkId } from '../../models';
import { DocumentLinkArea } from './sub-components/document-link-area.component';
import { MissingItemsDialog, MissingItem } from './sub-components/missing-items-dialog.component';
import { FinalizeConfirmDialog } from './sub-components/finalize-confirm-dialog.component';
import { SvgIconComponent } from '../../shared/svg-icon/svg-icon.component';
import type { LinkedFileItem } from '../../models/api';
import { DocMatrixService } from '../../services/doc-matrix.service';
import { UiStateService } from '../../services/ui-state.service';
import { StatusService, STATUS_LOCKED } from '../../services/status.service';
import {
    mapDocMatrixTreeNodes,
    applyCountsToTree,
    collectRequiredItemUuids,
    collectItemUuids,
    countRequiredItems,
    filterTree,
    filterRequiredOnly,
    filterTreeByVoucherId,
    buildCountMap,
} from '../../services/doc-matrix-tree.utils';

/** Ennyit vár a teljes linklista lekérésével, hogy a sorozatban érkező linkelések összevonódjanak. */
const REFRESH_COUNTS_DEBOUNCE_MS = 300;

@Component({
    selector: 'app-left-panel',
    imports: [
        CommonModule,
        FormsModule,
        TreeModule,
        InputTextModule,
        SelectModule,
        CheckboxModule,
        ButtonModule,
        IconFieldModule,
        InputIconModule,
        SharedModule,
        FloatLabelModule,
        DocumentLinkArea,
        MissingItemsDialog,
        FinalizeConfirmDialog,
        SvgIconComponent,
    ],
    templateUrl: './left-panel.html',
    styleUrl: './left-panel.scss',
})
export class LeftPanel implements OnInit, AfterViewInit {
    /** A fa darabszámainak frissítési igényei; a lekérés innen indul, összevonva. */
    private readonly refreshCounts$ = new Subject<{ settlementId: number; focusItemUuid?: string }>();

    private readonly docMatrixService = inject(DocMatrixService);
    private readonly uiState = inject(UiStateService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly statusService = inject(StatusService);

    private readonly treeWrapper = viewChild.required<ElementRef<HTMLElement>>('treeWrapper');
    private readonly tree = viewChild.required<Tree>('tree');

    @Output() filesLinked = new EventEmitter<LinkPatch>();
    @Output() linkFailed = new EventEmitter<void>();
    @Output() categorySelectionChange = new EventEmitter<boolean>();
    @Output() allLinksChange = new EventEmitter<LinkedFileItem[]>();
    @Output() navigateToFile = new EventEmitter<{ path: string; fileName: string }>();
    @Output() previewFile = new EventEmitter<{ path: string; fileName: string }>();
    @Output() matrixExpandedChange = new EventEmitter<boolean>();
    @Output() matrixEmptyChange = new EventEmitter<boolean>();
    @Output() lockStateChange = new EventEmitter<boolean>();
    @Output() settlementChange = new EventEmitter<number>();

    @Input() currentPath = 'tao://';

    matrixExpanded = signal(false);

    // Teljes képernyős (matrixExpanded) módban a jobb oldali oszlop tartalma:
    // a linkelt fájlok, vagy a szülőtől bevetített teljes fájlkezelő.
    fullscreenView = signal<'linked' | 'files'>('linked');

    // A fa virtual scroll viewport-magassága (px), a wrapper tényleges méretéből számolva.
    treeScrollHeight = signal('300px');

    // Header dropdowns
    selectedSettlement = signal<string | null>(null);
    settlementOptions = signal<DropdownOption[]>([]);

    // Filters
    searchText = '';
    selectedStatus: string | null = 'none';
    onlyMandatory = false;

    statusOptions: StatusOption[] = [
        { label: 'Válasszon', value: 'none', color: 'transparent' },
        { label: 'Elfogadva', value: 'accepted', color: '#34a853' },
        { label: 'Hiánypótlás alatt', value: 'pending', color: '#1a73e8' },
        { label: 'Jóváhagyás alatt', value: 'approval', color: '#f9ab00' },
        { label: 'Szerkesztés alatt', value: 'editing', color: '#ea4335' },
    ];

    // Category tree
    categories: TreeNode[] = [];
    categoriesOptions = signal<DropdownOption[]>([]);
    categoriesOptionsLabel = signal('');

    selectedNode = signal<TreeNode | null>(null);
    selectedCategoryLabel = signal<string | null>(null);
    // A kiválasztott elem közvetlen szülőjének neve (csak egy szint, nem teljes útvonal).
    selectedCategoryParentLabel = signal<string | null>(null);
    linkedFiles = signal<LinkedFile[]>([]);

    dragOverItemUuid = signal<string | null>(null);
    highlightedItemUuid = signal<string | null>(null);

    missingRequiredUuids = signal<string[]>([]);
    missingItems = signal<MissingItem[]>([]);
    missingItemsDialogVisible = signal(false);
    finalizeConfirmDialogVisible = signal(false);

    // Stats
    mandatoryTotal = signal(0);
    branchLinkedCount = signal(0);
    totalLinkedCount = signal(0);

    private allLinks: LinkedFileItem[] = [];
    private pendingFocusItemUuid: string | null = null;
    private sourcemodelType = 'settlement';
    voucherIdFilter = signal<number | null>(null);

    private get selectedSettlementName(): string {
        const value = this.selectedSettlement();
        return this.settlementOptions().find((option) => option.value === value)?.label ?? '';
    }

    private get settlementId(): number {
        const value = this.selectedSettlement();
        return value ? parseInt(value, 10) : 2;
    }

    get mandatoryPercent(): number {
        const total = this.mandatoryTotal();
        return total === 0 ? 0 : Math.min(100, Math.round((this.branchLinkedCount() / total) * 100));
    }

    private filterCache: { categoriesRef: TreeNode[]; key: string; result: TreeNode[] } | null = null;

    get filteredCategories(): TreeNode[] {
        const branchLabel = this.categoriesOptionsLabel();
        const search = this.searchText.trim().toLowerCase();
        const effectiveSearch = search.length >= 3 ? search : '';
        const key = `${branchLabel}|${this.onlyMandatory}|${effectiveSearch}`;

        if (this.filterCache && this.filterCache.categoriesRef === this.categories && this.filterCache.key === key) {
            return this.filterCache.result;
        }

        let nodes = this.categories;

        if (branchLabel) {
            const branch = this.categories.find((n) => n.label === branchLabel);
            nodes = branch?.children ?? [];
        }

        if (this.onlyMandatory) {
            nodes = filterRequiredOnly(nodes);
        }

        if (effectiveSearch) {
            nodes = filterTree(nodes, effectiveSearch);
        }

        this.filterCache = { categoriesRef: this.categories, key, result: nodes };
        return nodes;
    }

    ngOnInit(): void {
        this.refreshCounts$
            .pipe(
                debounceTime(REFRESH_COUNTS_DEBOUNCE_MS),
                switchMap((request) =>
                    this.docMatrixService.loadAllLinks('settlement', request.settlementId).pipe(
                        map((links) => ({ request, links })),
                        catchError((error) => {
                            console.error('Failed to refresh node counts:', error);
                            return EMPTY;
                        }),
                    ),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(({ request, links }) => {
                // Közben elszámolást válthattak — az akkori válasz már nem érvényes.
                if (request.settlementId !== this.settlementId) return;
                this.applyRefreshedLinks(links, request.focusItemUuid);
            });

        this.loadDocMatrixWithCounts();
    }

    ngAfterViewInit(): void {
        const el = this.treeWrapper().nativeElement;
        const handler = (event: Event) => this.onTreeDrop(event as DragEvent);
        el.addEventListener('drop', handler, true);
        this.destroyRef.onDestroy(() => el.removeEventListener('drop', handler, true));

        // A virtual scroll viewport-magasságát a wrapper tényleges magasságához igazítjuk,
        // és követjük az átméretezést (pl. panel-szélesség, ablak).
        const updateHeight = () => {
            const height = el.clientHeight;
            if (height > 0) this.treeScrollHeight.set(`${height}px`);
        };
        updateHeight();
        const observer = new ResizeObserver(updateHeight);
        observer.observe(el);
        this.destroyRef.onDestroy(() => observer.disconnect());
    }

    toggleMatrix(): void {
        this.matrixExpanded.update((v) => !v);
        if (!this.matrixExpanded()) {
            // Kis nézetben csak a linkelt fájlok férnek el a jobb oldali oszlopban.
            this.fullscreenView.set('linked');
        }
        this.matrixExpandedChange.emit(this.matrixExpanded());
    }

    onSettlementChange(): void {
        this.refreshMatrix();
    }

    refreshMatrix(): void {
        this.clearCategorySelection();
        this.docMatrixService.setDocMatrixConfig(this.sourcemodelType, this.settlementId).pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: () => this.loadDocMatrixWithCounts(),
            error: (error) => {
                this.pendingFocusItemUuid = null;
                console.error('Failed to set document matrix configuration:', error);
            },
        });
    }

    onCategoryTypeChange(): void {
        this.clearCategorySelection();
        this.computeLinkedCounts();
    }

    private clearCategorySelection(): void {
        if (this.selectedNode()) {
            this.selectedNode.set(null);
            this.selectedCategoryLabel.set(null);
            this.selectedCategoryParentLabel.set(null);
            this.linkedFiles.set([]);
            this.categorySelectionChange.emit(false);
        }
    }

    onSelectionChange(event: TreeNode | TreeNode[] | null | undefined): void {
        this.selectedNode.set(Array.isArray(event) ? null : (event ?? null));
    }

    onNodeSelect(event: { node: TreeNode }): void {
        if (event.node.data?.nodeType === 'item') {
            this.selectItemNode(event.node);
        }
    }

   
    onTreeKeydown(event: KeyboardEvent): void {
        if (event.code !== 'ArrowDown' && event.code !== 'ArrowUp') return;
        const row = document.activeElement;
        if (!row || !this.treeWrapper().nativeElement.contains(row)) return;
        const nodeEl = row.querySelector<HTMLElement>(':scope > .p-tree-node-content .left-panel__node');
        const uuid = nodeEl?.dataset['itemUuid'];
        if (!uuid) return;
        const node = this.findNodeByUuid(this.filteredCategories, uuid);
        if (node && node !== this.selectedNode()) {
            this.selectItemNode(node);
        }
    }

    private selectItemNode(node: TreeNode): void {
        this.selectedNode.set(node);
        this.selectedCategoryLabel.set(node.label ?? null);
        this.updateParentLabel(node.data?.uuid);
        if (node.data?.uuid) this.loadLinkedFiles(node.data.uuid);
        this.categorySelectionChange.emit(true);
    }

    // A szülőt mindig a teljes fából keressük ki: a megjelenített (szűrt) fa gyökere
    // egy kiválasztott ág lehet, ahol a szülő-lánc már hiányos lenne.
    private updateParentLabel(uuid: string | undefined): void {
        if (!uuid) {
            this.selectedCategoryParentLabel.set(null);
            return;
        }
        const path = this.findPathByUuid(this.categories, uuid);
        const parent = path && path.length > 1 ? path[path.length - 2] : null;
        this.selectedCategoryParentLabel.set(parent?.label ?? null);
    }

    onNodeUnselect(): void {
        this.selectedNode.set(null);
        this.selectedCategoryLabel.set(null);
        this.selectedCategoryParentLabel.set(null);
        this.linkedFiles.set([]);
        this.categorySelectionChange.emit(false);
    }

    linkFiles(files: FileItem[]): void {
        this.processFiles(files);
    }

    refreshLinkedFiles(): void {
        const uuid = this.selectedNode()?.data?.uuid;
        if (uuid) this.loadLinkedFiles(uuid);
    }

    validateAttachments(): void {
        this.statusService.validate(this.sourcemodelType, this.settlementId).pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: (result) => {
                const uuids = result.missing_items ?? [];
                this.missingRequiredUuids.set(uuids);
                // Aktív keresés esetén a kereső kiszűrhet kötelező elemeket a fából,
                // így a rájuk kattintás nem tudna navigálni. Ilyenkor csak azokat a
                // hiányzó elemeket listázzuk, amelyek a szűrt fában is láthatóak.
                const search = this.searchText.trim().toLowerCase();
                const effectiveSearch = search.length >= 3 ? search : '';
                let visibleUuids = uuids;
                if (effectiveSearch) {
                    const searchable = collectItemUuids(filterTree(this.categories, effectiveSearch));
                    visibleUuids = uuids.filter((uuid) => searchable.has(uuid));
                }
                this.missingItems.set(visibleUuids.map((uuid) => ({
                    uuid,
                    name: this.findNodeByUuid(this.categories, uuid)?.label ?? uuid,
                    sourceModelName: this.findNodeByUuid(this.categories, uuid)?.leaf
                })));
                if (uuids.length > 0) {
                    this.missingItemsDialogVisible.set(true);
                } else {
                    // Nincs hiányzó kötelező elem — felajánljuk a véglegesítést.
                    this.finalizeConfirmDialogVisible.set(true);
                }
            },
            error: (error) => console.error('Failed to validate attachments:', error),
        });
    }

    onNavigateToMissingItem(uuid: string): void {
        this.missingItemsDialogVisible.set(false);
        this.highlightDocMatrixItem(uuid);
    }

    onFinalizeConfirmed(): void {
        this.finalizeConfirmDialogVisible.set(false);
        this.finalizeAttachments();
    }

    finalizeAttachments(): void {
        this.statusService.lock(this.sourcemodelType, this.settlementId).pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: (response) => {
                // A backend 200-as válasszal, de { status: 'error' } törzzsel jelzi, hogy
                // még vannak nem társított kötelező elemek — ilyenkor NEM zárjuk le (a
                // gombot sem állítjuk át), hanem felhozzuk a hiányzó elemek popupot.
                if ((response as { status?: string } | null)?.status === 'error') {
                    this.validateAttachments();
                    return;
                }
                this.lockStateChange.emit(true);
            },
            error: (error) => {
                if (error instanceof HttpErrorResponse && error.error?.status === 'error') {
                    this.validateAttachments();
                    return;
                }
                console.error('Failed to finalize attachments:', error);
            },
        });
    }

    reopenAttachments(): void {
        this.statusService.unlock(this.sourcemodelType, this.settlementId).pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: () => this.lockStateChange.emit(false),
            error: (error) => console.error('Failed to reopen attachments:', error),
        });
    }

    /**
     * A megadott elemre ugrik (kiválasztás + kiemelés + odagörgetés). Ha az elem egy másik —
     * jellemzően már lezárt — elszámoláshoz tartozik, előbb elszámolást váltunk, és a mátrix
     * újratöltése után folytatódik a kiemelés (`pendingFocusItemUuid`).
     */
    highlightDocMatrixItem(uuid: string, sourcemodelId?: number | null): void {
        if (sourcemodelId != null && sourcemodelId !== this.settlementId) {
            const option = this.settlementOptions().find((item) => item.value === String(sourcemodelId));
            // Bizonylat-szűrő esetén az elszámolás kötött (a választó is tiltott), ilyenkor
            // marad az aktuális fa: ha az elem nincs benne, a kiemelés egyszerűen elmarad.
            if (option && this.voucherIdFilter() === null) {
                this.pendingFocusItemUuid = uuid;
                this.selectedSettlement.set(option.value);
                this.refreshMatrix();
                return;
            }
        }
        const owningBranch = this.categories.find((branch) => this.findNodeByUuid(branch.children ?? [], uuid));
        if (owningBranch?.label && owningBranch.label !== this.categoriesOptionsLabel()) {
            this.categoriesOptionsLabel.set(owningBranch.label);
            this.computeLinkedCounts();
        }
        const displayedRoots = this.filteredCategories;
        const path = this.findPathByUuid(displayedRoots, uuid);
        const node = path?.[path.length - 1] ?? null;
        if (node) {
            // A cél szülő-láncát kinyitjuk (a node maga marad, ahogy volt), különben
            // becsukott csoportban a node nincs a DOM-ban és nem tudunk odagörgetni.
            for (let i = 0; i < path!.length - 1; i++) {
                path![i].expanded = true;
            }
            this.selectedNode.set(node);
            this.selectedCategoryLabel.set(node.label ?? null);
            this.updateParentLabel(uuid);
            this.loadLinkedFiles(uuid);
            this.categorySelectionChange.emit(true);
        }
        this.highlightedItemUuid.set(uuid);
        setTimeout(() => {
            // Virtual scrollnál a cél node lehet, hogy nincs a DOM-ban, ezért a lapos
            // (látható) listában elfoglalt indexe alapján görgetünk hozzá.
            const index = this.computeVisibleIndex(this.filteredCategories, uuid);
            if (index >= 0) this.tree().scrollToVirtualIndex(index);
        }, 0);
        setTimeout(() => this.highlightedItemUuid.set(null), 3000);
    }

    onFilesDropped(files: FileItem[]): void {
        this.processFiles(files);
    }

    private resolveItemUuid(event: DragEvent): string | null {
        const target = event.target as HTMLElement | null;
        const content = target?.closest('.p-tree-node-content');
        const nodeEl = content?.querySelector<HTMLElement>('.left-panel__node');
        return nodeEl?.dataset['itemUuid'] || null;
    }

    onTreeDragOver(event: DragEvent): void {
        if (!this.uiState.isInternalDragging()) {
            if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
            return;
        }

        const uuid = this.resolveItemUuid(event);
        if (!uuid) {
            this.dragOverItemUuid.set(null);
            if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
            return;
        }

        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
        this.dragOverItemUuid.set(uuid);
    }

    onTreeDragLeave(event: DragEvent): void {
        const related = event.relatedTarget as Node | null;
        const wrapper = event.currentTarget as HTMLElement;
        if (!related || !wrapper.contains(related)) {
            this.dragOverItemUuid.set(null);
        }
    }

    onTreeDrop(event: DragEvent): void {
        this.dragOverItemUuid.set(null);
        if (!this.uiState.isInternalDragging()) return;

        const uuid = this.resolveItemUuid(event);
        if (!uuid) return;

        event.preventDefault();

        const data = event.dataTransfer?.getData('application/json');
        if (!data) return;
        try {
            const files: FileItem[] = JSON.parse(data);
            if (Array.isArray(files) && files.length > 0) {
                this.processFiles(files, uuid);
            }
        } catch (e) {
            console.error('Error parsing dropped files data:', e);
        }
    }

    onFileUnlink(file: LinkedFile): void {
        this.unlinkFiles([file]);
    }

    onMassUnlink(files: LinkedFile[]): void {
        this.unlinkFiles(files);
    }

    private loadDocMatrixWithCounts(): void {
        this.docMatrixService.loadDocMatrix().pipe(
            takeUntilDestroyed(this.destroyRef),
            switchMap((docmatrix) => {
                const meta = docmatrix?.docmatrix?.meta;
                this.sourcemodelType = meta?.sourcemodel_type ?? 'settlement';
                this.voucherIdFilter.set(docmatrix.filtering?.voucher_id ?? null);
                if (meta?.sourcemodels) {
                    this.settlementOptions.set(meta.sourcemodels.map((s) => ({
                        label: s.displayed_name,
                        value: String(s.sourcemodel_id),
                    })));
                    this.selectedSettlement.set(String(meta.sourcemodel_id));
                }
                // A zárolás-állapot csak a sourcemodel-től függ, a linkektől nem, ezért a két
                // kérés párhuzamosan mehet. A fát már itt kiszűrjük: a `mapDocMatrixTreeNodes`
                // 1:1 képez, így az elemszámból eldönthető, kell-e egyáltalán státusz-kérés.
                let treeItems = docmatrix?.docmatrix?.tree?.items ?? [];
                const voucherId = this.voucherIdFilter();
                if (voucherId !== null) {
                    treeItems = filterTreeByVoucherId(treeItems, voucherId);
                }

                return forkJoin({
                    links: this.docMatrixService.loadAllLinks('settlement', this.settlementId),
                    locked: treeItems.length > 0
                        ? this.statusService.getStatus(this.sourcemodelType, this.settlementId).pipe(
                            map((result) => result.state === STATUS_LOCKED),
                            catchError((error) => {
                                console.error('Failed to load lock status:', error);
                                return of(null);
                            }),
                        )
                        : of(null),
                }).pipe(
                    map(({ links, locked }) => ({ links, locked, treeItems })),
                );
            }),
        ).subscribe({
            next: ({ links, locked, treeItems }) => {
                this.settlementChange.emit(this.settlementId);
                links = this.applyVoucherFilter(links);
                this.allLinks = links;
                this.allLinksChange.emit(links);
                const counts = buildCountMap(links);
                this.categories = mapDocMatrixTreeNodes(treeItems, 0, counts);
                this.matrixEmptyChange.emit(this.categories.length === 0);
                if (locked !== null) {
                    this.lockStateChange.emit(locked);
                }
                const options = this.categories
                    .filter((item) => item.label !== undefined)
                    .map((item) => ({ label: item.label!, value: item.label! }));
                this.categoriesOptions.set(options);
                this.categoriesOptionsLabel.set(options[0]?.value ?? '');
                this.computeLinkedCounts();

                // Elszámolás-váltással indított odaugrás: most már a cél elszámolás fája van betöltve.
                const focusUuid = this.pendingFocusItemUuid;
                this.pendingFocusItemUuid = null;
                if (focusUuid) this.highlightDocMatrixItem(focusUuid);
            },
            error: (error) => {
                console.error('Failed to load document matrix:', error);
                this.pendingFocusItemUuid = null;
                this.categories = [];
                this.matrixEmptyChange.emit(true);
            },
        });
    }

    /**
     * A kijelölt kategória linkjeit a már betöltött teljes linklistából állítja elő.
     * Az `allLinks` ugyanarra az elszámolásra vonatkozik és már át van szűrve a
     * bizonylat-szűrőn, ezért ehhez nem kell külön lekérés.
     */
    private setLinkedFilesForItem(itemUuid: string): void {
        this.linkedFiles.set(
            this.allLinks
                .filter((item) => item.item_uuid === itemUuid)
                .map((item) => this.toLinkedFile(item)),
        );
    }

    private toLinkedFile(item: LinkedFileItem): LinkedFile {
        return {
            name: item.file.name,
            extension: item.file.name.includes('.') ? (item.file.name.split('.').pop() ?? '') : '',
            statusColor: '#1a73e8',
            linkId: item.id,
            fileId: item.file.filemanager_file_id,
            filePath: item.file.path,
        };
    }

    private loadLinkedFiles(itemUuid: string): void {
        this.linkedFiles.set([]);
        this.docMatrixService.loadLinkedFiles('settlement', this.settlementId, itemUuid).pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: (items) => {
                this.linkedFiles.set(this.applyVoucherFilter(items).map((item) => this.toLinkedFile(item)));
            },
            error: (error) => console.error('Failed to load linked files:', error),
        });
    }

    private findNodeByUuid(nodes: TreeNode[], uuid: string): TreeNode | null {
        for (const node of nodes) {
            if (node.data?.uuid === uuid) return node;
            if (node.children) {
                const found = this.findNodeByUuid(node.children, uuid);
                if (found) return found;
            }
        }
        return null;
    }

    // A gyökértől a keresett node-ig vezető útvonal (a node-ot is beleértve), vagy null.
    private findPathByUuid(nodes: TreeNode[], uuid: string): TreeNode[] | null {
        for (const node of nodes) {
            if (node.data?.uuid === uuid) return [node];
            if (node.children) {
                const childPath = this.findPathByUuid(node.children, uuid);
                if (childPath) return [node, ...childPath];
            }
        }
        return null;
    }

    // A cél node indexe a virtual scroll lapos listájában: pre-order bejárás, ahol egy
    // node gyerekei csak akkor számítanak, ha a node ki van nyitva (expanded).
    private computeVisibleIndex(nodes: TreeNode[], uuid: string): number {
        let count = 0;
        let found = -1;
        const walk = (list: TreeNode[]): void => {
            for (const node of list) {
                if (found >= 0) return;
                if (node.data?.uuid === uuid) {
                    found = count;
                    return;
                }
                count++;
                if (node.expanded && node.children?.length) {
                    walk(node.children);
                }
            }
        };
        walk(nodes);
        return found;
    }

    private processFiles(files: FileItem[], targetItemUuid?: string): void {
        const itemUuid = targetItemUuid ?? this.selectedNode()?.data?.uuid;
        if (!itemUuid) {
            console.error('No category selected. Cannot link files.');
            return;
        }

        const filesToLink = files.filter((file) => file.type === 'file' && !!file.apiPath);
        if (filesToLink.length === 0) {
            console.error('No valid files with apiPath available to link.');
            return;
        }

        const itemLabel = targetItemUuid
            ? this.findNodeByUuid(this.categories, targetItemUuid)?.label
            : this.selectedNode()?.label;

        const payloads = filesToLink
            .filter((file): file is FileItem & { id: number } => file.id !== undefined)
            .map((file) => ({
                filemanager_file_id: file.id,
                sourcemodel_type: this.sourcemodelType,
                sourcemodel_id: this.settlementId,
                displayed_name: itemLabel ?? '',
                item_uuid: itemUuid,
            }));

        this.docMatrixService.linkFiles(payloads).pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: () => {
                // A kijelölt kategória listáját nem kérjük le külön: a refreshNodeCounts
                // úgyis lehúzza a teljes linklistát, amiben ezek a linkek benne vannak.
                this.refreshNodeCounts(itemUuid);
                this.filesLinked.emit({
                    kind: 'link',
                    fileIds: payloads.map((payload) => payload.filemanager_file_id),
                    link: {
                        id: nextTempLinkId(),
                        sourcemodel_id: this.settlementId,
                        sourcemodel_type: this.sourcemodelType,
                        sourcemodel_name: this.selectedSettlementName,
                        displayed_name: itemLabel ?? '',
                        item_uuid: itemUuid,
                    },
                });
            },
            error: (error) => {
                console.error('Failed to link files:', error);
                this.refreshNodeCounts();
                this.linkFailed.emit();
            },
        });
    }

    private unlinkFiles(files: LinkedFile[]): void {
        const selectedNode = this.selectedNode();
        if (!selectedNode?.data?.uuid) {
            console.warn('No selected category found for unlinking. Removing locally.');
            files.forEach(file => this.removeLinkedFile(file));
            return;
        }

        const payloads = files
            .filter((file): file is LinkedFile & { fileId: number } => file.fileId !== undefined)
            .map((file) => ({
                filemanager_file_id: file.fileId,
                sourcemodel_type: this.sourcemodelType,
                sourcemodel_id: this.settlementId,
                item_uuid: selectedNode.data.uuid,
            }));

        this.docMatrixService.unlinkFiles(payloads).pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: () => {
                files.forEach(file => this.removeLinkedFile(file));
                this.refreshNodeCounts();
                this.filesLinked.emit({
                    kind: 'unlink',
                    fileIds: payloads.map((payload) => payload.filemanager_file_id),
                    sourcemodelId: this.settlementId,
                    itemUuid: selectedNode.data.uuid,
                });
            },
            error: (error) => {
                console.error('Failed to unlink files:', error);
                this.refreshNodeCounts();
                this.linkFailed.emit();
            },
        });
    }

    /**
     * Kéri a fa darabszámainak frissítését. Egymás után gyorsan érkező linkelések esetén a
     * teljes linklista lekérése összevonódik: csak a sorozat végén megy ki egyetlen kérés.
     * A futó lekérést is felváltja egy újabb igény, így közbenső válasz nem érkezik.
     */
    private refreshNodeCounts(focusItemUuid?: string): void {
        this.refreshCounts$.next({ settlementId: this.settlementId, focusItemUuid });
    }

    private applyRefreshedLinks(links: LinkedFileItem[], focusItemUuid?: string): void {
        links = this.applyVoucherFilter(links);
        this.allLinks = links;
        this.allLinksChange.emit(links);
        this.categories = applyCountsToTree(this.categories, buildCountMap(links));

        const selectedUuid = this.selectedNode()?.data?.uuid;
        if (selectedUuid) {
            const node = this.findNodeByUuid(this.filteredCategories, selectedUuid);
            if (node) this.selectedNode.set(node);
            this.setLinkedFilesForItem(selectedUuid);
        }
        this.computeLinkedCounts();
        if (focusItemUuid) {
            setTimeout(() => this.focusTreeItem(focusItemUuid));
        }
    }

    private focusTreeItem(uuid: string): void {
        const row = this.treeWrapper().nativeElement
            .querySelector(`.left-panel__node[data-item-uuid="${uuid}"]`)
            ?.closest<HTMLElement>('li[role="treeitem"]');
        if (row) {
            row.tabIndex = 0;
            row.focus();
        }
    }

    private computeLinkedCounts(): void {
        this.totalLinkedCount.set(this.allLinks.length);
        this.mandatoryTotal.set(countRequiredItems(this.categories));

        const requiredUuids = collectRequiredItemUuids(this.categories);
        const uploadedRequired = new Set(
            this.allLinks.map((l) => l.item_uuid).filter((uuid) => requiredUuids.has(uuid)),
        );
        this.branchLinkedCount.set(uploadedRequired.size);
    }

    private applyVoucherFilter(links: LinkedFileItem[]): LinkedFileItem[] {
        const voucherId = this.voucherIdFilter();
        if (voucherId === null) return links;
        return links.filter((link) => link.context?.voucher_id === voucherId);
    }

    private removeLinkedFile(file: LinkedFile): void {
        this.linkedFiles.update((current) => current.filter((f) => f.linkId !== file.linkId));
    }
}
